import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, withQuery, quantity } from '../api.js';
import GroupPicker from '../components/GroupPicker.jsx';

/**
 * Ревизия: сверяване на преброеното с това, което пише в системата.
 *
 * Пишеш само колко си преброил. Разликата се смята сама — тя е това,
 * което тръгва към склада, — а редовете без разлика изобщо не влизат в
 * документа. Иначе всяка ревизия щеше да е стотици редове с нули.
 *
 * Ревизията е и единственият път нагоре: забраната за отрицателни
 * наличности спира продажба, чиято стока „я няма" по документи, и точно
 * оттук се оправя.
 */
export default function Revision() {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState([]);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [groupId, setGroupId] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [groupBusy, setGroupBusy] = useState(false);
  const [hideZero, setHideZero] = useState(false);
  const search = useRef(null);

  useEffect(() => {
    api.get('/api/warehouses')
      .then((w) => { setWarehouses(w); setWarehouseId(String(w[0]?.id ?? '')); })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (query.trim().length < 2 || !warehouseId) { setHits([]); return undefined; }
    const t = setTimeout(() => {
      api.get(withQuery('/api/products', {
        q: query, warehouseId: Number(warehouseId), limit: 8, inStock: false,
      }))
        .then((r) => setHits(r.items.filter((p) => Number(p.is_stocked) !== 0)))
        .catch(() => setHits([]));
    }, 220);
    return () => clearTimeout(t);
  }, [query, warehouseId]);

  async function loadGroup(id) {
    setGroupBusy(true);
    setError(null);
    try {
      const res = await api.get(withQuery('/api/reports/revision-sheet', {
        warehouseId: Number(warehouseId), groupId: id,
      }));
      setGroupId(id);
      setGroupName(res.group?.name ?? '');
      setLines((ls) => {
        const known = new Set(ls.map((l) => l.productId));
        const added = res.items
          .filter((p) => !known.has(p.product_id))
          .map((p) => ({
            key: `${p.product_id}-${Date.now()}-${Math.random()}`,
            productId: p.product_id,
            description: p.name,
            unit: p.unit,
            note: p.note ?? '',
            properties: p.properties_text ?? '',
            expected: Number(p.quantity ?? 0),
            counted: '',
          }));
        return [...ls, ...added];
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setGroupBusy(false);
    }
  }

  function add(p) {
    if (lines.some((l) => l.productId === p.id)) { setQuery(''); setHits([]); return; }
    setLines((ls) => [...ls, {
      key: `${p.id}-${Date.now()}`,
      productId: p.id,
      description: p.name,
      unit: p.unit,
      note: p.note ?? '',
      properties: p.properties_text ?? '',
      // Каквото системата смята, че има — срещу него се брои.
      expected: Number(p.quantity ?? 0),
      counted: '',
    }]);
    setQuery('');
    setHits([]);
    search.current?.focus();
  }

  const patch = (key, v) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...v } : l)));

  const withDelta = lines.map((l) => ({
    ...l,
    delta: l.counted === '' ? 0 : Math.round((Number(l.counted) - l.expected) * 10000) / 10000,
  }));
  const changed = withDelta.filter((l) => l.delta !== 0);
  // Само за екрана — крие редовете, за които базата не пази наличност
  // по документи (0 бр.), без да ги маха от самата ревизия.
  const shownRows = hideZero ? withDelta.filter((l) => l.expected !== 0) : withDelta;
  const ready = changed.length > 0 && !busy;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/api/documents', {
        docType: 'revision',
        status: 'confirmed',
        warehouseId: Number(warehouseId),
        note: note || 'Ревизия',
        // Количеството по реда е самата разлика — така я разбира складът.
        items: changed.map((l) => ({
          productId: l.productId,
          description: l.description,
          unit: l.unit,
          quantity: l.delta,
          price: 0,
          vatRate: 0,
        })),
      });
      navigate(`/documents/${res.id}`);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head"><h1>Ревизия</h1></div>

      {error && <div className="error">{error}</div>}

      <div className="card">
        <div className="card-body">
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label className="field">
              Склад
              <select value={warehouseId} onChange={(e) => {
                setWarehouseId(e.target.value);
                setLines([]);
                setGroupId(null);
                setGroupName('');
              }}>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </label>
            <label className="field" style={{ flex: 1, minWidth: 240 }}>
              Забележка
              <input value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="например: месечна ревизия на рафт 3" />
            </label>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">Ревизия по група</div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="hint" style={{ marginRight: 4 }}>
              Зарежда всички стоки от групата (и подгрупите ѝ) с текущата им наличност —
              и наличните, и нулевите.
            </span>
            <GroupPicker
              label="избери група…"
              value={groupId}
              valueLabel={groupName}
              onPick={(id) => { if (id != null && warehouseId) loadGroup(id); }}
              onClear={() => { setGroupId(null); setGroupName(''); }}
            />
            {groupBusy && <span className="muted">зареждане…</span>}
            {groupId && (
              <a
                href={withQuery('/print/revision', { warehouseId: Number(warehouseId), groupId })}
                target="_blank" rel="noreferrer"
              >
                <button type="button">Печат на лист за преброяване</button>
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">Броене</div>
        <div className="card-body" style={{ paddingBottom: 8 }}>
          <label className="field" style={{ position: 'relative', maxWidth: 460 }}>
            Добавяне на стока
            <span className="hint">пиши само преброеното — разликата се смята сама</span>
            <input ref={search} type="search" autoComplete="off"
              value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="търсене по име, код или бърз код" />
            {hits.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 6, boxShadow: 'var(--shadow)', overflow: 'hidden',
              }}>
                {hits.map((p) => (
                  <div key={p.id} style={{ padding: '8px 11px', cursor: 'pointer' }}
                    onMouseDown={(e) => { e.preventDefault(); add(p); }}>
                    <strong>{p.name}</strong>
                    <span className="badge muted" style={{ marginLeft: 8 }}>
                      по документи {quantity(p.quantity ?? 0)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </label>
          <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 10 }}>
            <input type="checkbox" checked={hideZero}
              onChange={(e) => setHideZero(e.target.checked)} />
            Скрий стоките без наличност по документи (0 бр.)
          </label>
        </div>

        <div className="table-wrap">
          <table className="line-editor">
            <thead>
              <tr>
                <th className="num" style={{ width: 34 }}>№</th>
                <th>Наименование</th>
                <th>Бележка</th>
                <th>Характеристики</th>
                <th className="num" style={{ width: 130 }}>По документи</th>
                <th className="num" style={{ width: 120 }}>Преброено</th>
                <th className="num" style={{ width: 110 }}>Разлика</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {shownRows.map((l, index) => (
                <tr key={l.key}>
                  <td className="num muted">{index + 1}</td>
                  <td>{l.description}</td>
                  <td className="muted">{l.note}</td>
                  <td className="muted">{l.properties}</td>
                  <td className="num muted">{quantity(l.expected)} {l.unit}</td>
                  <td className="num">
                    <input className="qty num" type="number" step="0.001" min="0"
                      value={l.counted} placeholder="—"
                      onChange={(e) => patch(l.key, { counted: e.target.value })} />
                  </td>
                  <td className="num">
                    {l.counted === '' ? <span className="muted">—</span>
                      : l.delta === 0 ? <span className="muted">няма</span>
                        : (
                          <strong style={{
                            color: l.delta > 0 ? 'var(--ok)' : 'var(--danger)',
                          }}>
                            {l.delta > 0 ? '+' : ''}{quantity(l.delta)}
                          </strong>
                        )}
                  </td>
                  <td>
                    <button className="link" title="махни реда"
                      onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>×</button>
                  </td>
                </tr>
              ))}
              {lines.length === 0 && (
                <tr><td colSpan={8} className="empty">Добавете стока, за да започнете</td></tr>
              )}
              {lines.length > 0 && shownRows.length === 0 && (
                <tr><td colSpan={8} className="empty">Няма редове — премахнете отметката „Скрий нулевите"</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="card-body">
          <div style={{
            display: 'flex', gap: 12, justifyContent: 'flex-end',
            alignItems: 'center', flexWrap: 'wrap',
          }}>
            <span className="hint" style={{ textAlign: 'right' }}>
              {changed.length === 0
                ? 'Редовете без разлика не влизат в документа.'
                : `Ще се коригират ${changed.length} ${changed.length === 1 ? 'ред' : 'реда'}.`}
            </span>
            <button className="primary" onClick={save} disabled={!ready}>
              Запис на ревизията
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
